import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Users, Upload, Edit2, Trash2, Globe } from 'lucide-react';

const AdminTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    position_de: '',
    position_en: '',
    languages: '',
    file: null,
    previewUrl: null
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/team/admin');
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError('Fehler beim Laden der Team-Mitglieder');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Bitte wählen Sie eine Bilddatei aus');
        return;
      }
      setFormData({
        ...formData,
        file: file,
        previewUrl: URL.createObjectURL(file)
      });
      setError('');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.file) {
      setError('Bitte wählen Sie ein Foto aus');
      return;
    }

    if (!formData.name || !formData.position_de || !formData.position_en || !formData.languages) {
      setError('Bitte füllen Sie alle Felder aus');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');

      const data = new FormData();
      data.append('file', formData.file);

      await api.post(
        `/team/upload?name=${encodeURIComponent(formData.name)}&position_de=${encodeURIComponent(formData.position_de)}&position_en=${encodeURIComponent(formData.position_en)}&languages=${encodeURIComponent(formData.languages)}`,
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSuccess('Team-Mitglied erfolgreich hinzugefügt!');
      setFormData({
        name: '',
        position_de: '',
        position_en: '',
        languages: '',
        file: null,
        previewUrl: null
      });
      fetchTeamMembers();
    } catch (err) {
      console.error('Error uploading team member:', err);
      setError(err.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Möchten Sie dieses Team-Mitglied wirklich löschen?')) {
      return;
    }

    try {
      await api.delete(`/team/${memberId}`);
      setSuccess('Team-Mitglied erfolgreich gelöscht!');
      fetchTeamMembers();
    } catch (err) {
      console.error('Error deleting team member:', err);
      setError('Fehler beim Löschen');
    }
  };

  const startEdit = (member) => {
    setEditingId(member.id);
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (memberId, updates) => {
    try {
      await api.put(`/team/${memberId}`, updates);
      setSuccess('Team-Mitglied aktualisiert!');
      setEditingId(null);
      fetchTeamMembers();
    } catch (err) {
      console.error('Error updating team member:', err);
      setError('Fehler beim Aktualisieren');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <Users className="w-8 h-8 mr-3 text-blue-600" />
        <h1 className="text-3xl font-bold">Team verwalten</h1>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Upload Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Upload className="w-5 h-5 mr-2" />
          Neues Team-Mitglied hinzufügen
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="z.B. Max Mustermann"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Sprachen *</label>
              <input
                type="text"
                name="languages"
                value={formData.languages}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="z.B. Deutsch, English, Arabic"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Position (Deutsch) *</label>
              <input
                type="text"
                name="position_de"
                value={formData.position_de}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="z.B. Kitesurf Trainer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Position (English) *</label>
              <input
                type="text"
                name="position_en"
                value={formData.position_en}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Kitesurf Instructor"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Foto auswählen *</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {formData.previewUrl && (
            <div className="mb-4">
              <p className="text-sm font-medium mb-2">Vorschau:</p>
              <img 
                src={formData.previewUrl} 
                alt="Preview" 
                className="w-32 h-32 object-cover rounded-lg shadow-md"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={uploading}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {uploading ? 'Hochladen...' : 'Team-Mitglied hinzufügen'}
          </button>
        </form>
      </div>

      {/* Team Members List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Team-Mitglieder</h2>

        {loading ? (
          <div className="text-center py-8">Laden...</div>
        ) : teamMembers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Keine Team-Mitglieder vorhanden. Fügen Sie das erste hinzu!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teamMembers.map(member => (
              <div key={member.id} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api${member.photo_url}`}
                  alt={member.name}
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    console.error('Image failed to load:', member.photo_url);
                    e.target.src = 'https://via.placeholder.com/400x300?text=Team+Member';
                  }}
                />
                <div className="p-4">
                  {editingId === member.id ? (
                    <EditForm
                      member={member}
                      onSave={(updates) => saveEdit(member.id, updates)}
                      onCancel={cancelEdit}
                    />
                  ) : (
                    <>
                      <h3 className="text-lg font-bold mb-1">{member.name}</h3>
                      <p className="text-sm text-gray-600 mb-1">
                        🇩🇪 {member.position_de}
                      </p>
                      <p className="text-sm text-gray-600 mb-2">
                        🇬🇧 {member.position_en}
                      </p>
                      <div className="flex items-center text-sm text-gray-500 mb-3">
                        <Globe className="w-4 h-4 mr-1" />
                        {member.languages}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEdit(member)}
                          className="flex-1 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 flex items-center justify-center"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="flex-1 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 flex items-center justify-center"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Löschen
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EditForm = ({ member, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: member.name,
    position_de: member.position_de,
    position_en: member.position_en,
    languages: member.languages
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <input
        type="text"
        name="name"
        value={formData.name}
        onChange={handleChange}
        className="w-full px-2 py-1 border rounded text-sm"
        placeholder="Name"
      />
      <input
        type="text"
        name="position_de"
        value={formData.position_de}
        onChange={handleChange}
        className="w-full px-2 py-1 border rounded text-sm"
        placeholder="Position (DE)"
      />
      <input
        type="text"
        name="position_en"
        value={formData.position_en}
        onChange={handleChange}
        className="w-full px-2 py-1 border rounded text-sm"
        placeholder="Position (EN)"
      />
      <input
        type="text"
        name="languages"
        value={formData.languages}
        onChange={handleChange}
        className="w-full px-2 py-1 border rounded text-sm"
        placeholder="Sprachen"
      />
      <div className="flex space-x-2">
        <button
          type="submit"
          className="flex-1 bg-green-600 text-white px-2 py-1 rounded text-sm hover:bg-green-700"
        >
          Speichern
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-600 text-white px-2 py-1 rounded text-sm hover:bg-gray-700"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
};

export default AdminTeam;
